import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import Authorize from '@components/Authorize';
import { API_DEFAULT_PREFIX, API_DEFAULT_VERSION, FORM_MODE, Permissions, REST_API } from '@awp/constants';
import {
  ActionMenuDelete,
  ActionMenuDownload,
  ActionMenuEdit,
  ActionMenuView,
} from '@components/Common/AwpActionMenus';
import React from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import {
  FormCheckbox,
  FormDropZone,
  FormInput,
  FormSelect,
  FormTagsInput,
  FormTextArea,
} from '@components/Common/AwpFormInputs';
import { bytesToMB } from '@components/Avalanche/SupportingWeatherForecast/helpers';
import * as Yup from 'yup';
import { DeleteJson, GetJson } from '@awp/helpers/fetchReact';
import { CrudEntity, CrudService, EntityProps } from '@awp/shared/CrudService';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { loadImage } from '@awp/helpers/UtilsTypescript';
import { format as formatDate } from 'date-fns';

export interface AvalanchePathImagesDTO {
  Id: number;
  GeographicAreaId: number;
  OriginalObjectImage: string;
  ReducedObjectImage: string;
  IsDefault: boolean;
  Name: string;
  Notes: string;
  Tags: string[];
  LastUpdatetimestamp: string;
  LastUpdateUserId: string;
  images: any[];
}

export const AVALANCHE_PATH_IMAGES_PARAMS = {
  SORT_BY: 'sortBy',
  SEARCH: 'search',
};

class AvalanchePathImagesService extends CrudService {
  constructor(crudEntity?: CrudEntity) {
    super(crudEntity);
  }

  public targetSize = 52428800;

  // TODO: Change to public method
  static getAvalanchePathImages = async (entityId: string) => {
    const url = `${REST_API.AVALANCHE_PATH}${entityId}/images`;
    return GetJson(url).then((response) => response.json());
  };

  public getAvalanchePathImagesById = async (parentId: string, entityId: string) => {
    const url = `${REST_API.AVALANCHE_PATH}${parentId}/images/${entityId}`;
    return GetJson(url).then((response) => response.json());
  };

  public putAvalanchePathImage = async (parentEntityId: string, entity: AvalanchePathImagesDTO) => {
    const url = `${API_DEFAULT_PREFIX}${API_DEFAULT_VERSION}${REST_API.AVALANCHE_PATH}${parentEntityId}/images/${entity?.Id}`;
    const formData = this.mapFormToFormData(entity);

    return fetch(url, {
      method: 'PUT',
      body: formData,
    });
  };

  public postAvalanchePathImage = async (parentEntityId: string, entity: AvalanchePathImagesDTO) => {
    const url = `${API_DEFAULT_PREFIX}${API_DEFAULT_VERSION}${REST_API.AVALANCHE_PATH}${parentEntityId}/images`;

    entity.GeographicAreaId = Number(parentEntityId);
    const formData = this.mapFormToFormData(entity);

    return fetch(url, {
      method: 'POST',
      body: formData,
    });
  };

  // TODO: Change to public method
  static deleteAvalanchePathImage = async (entityId: string, imageId: string) => {
    const url = `${REST_API.AVALANCHE_PATH}${entityId}/images/${imageId}`;
    return DeleteJson(url);
  };

  public mapFormToFormData(entity: AvalanchePathImagesDTO): FormData {
    const formData = new FormData();

    if (entity.Id) formData.append('Id', entity.Id.toString());
    formData.append('Name', entity.Name);
    formData.append('Notes', entity.Notes);
    formData.append('IsDefault', entity.IsDefault.toString());
    formData.append('GeographicAreaId', entity.GeographicAreaId.toString());

    entity.Tags?.forEach((tag: string) => formData.append('Tags', tag));
    entity.images?.forEach((image: any) => formData.append('images', image));

    return formData;
  }

  public hasSearch = (src: string, search: string) => {
    return src ? src.toLowerCase().includes(search.toLowerCase()) : false;
  };

  public filterData(data: AvalanchePathImagesDTO[], sortBy: string | null, search: string): AvalanchePathImagesDTO[] {
    let filtered = [...data];
    if (sortBy) {
      filtered.sort((a: AvalanchePathImagesDTO, b: AvalanchePathImagesDTO) => {
        return Date.parse(a.LastUpdatetimestamp) - Date.parse(b.LastUpdatetimestamp);
      });

      if (sortBy === 'desc') filtered.reverse();
    }

    if (!isValueEmpty(search)) {
      filtered = filtered.filter((f: AvalanchePathImagesDTO) => this.hasSearch(f.Name, search));
    }

    return filtered;
  }

  public handleDownload = (entity: AvalanchePathImagesDTO) => {
    const imageUrl = this.getImageUrl(entity);

    const tempLink = document.createElement('a');
    tempLink.href = imageUrl;
    tempLink.download = `${entity.Name}.jpg`;
    tempLink.click(); // Download the image
  };

  getColumns(props: any) {
    const { setShowEditModal, setEntity, setShowDeleteModal, mode, ...rest } = props;

    return [
      { Header: 'Name', accessor: 'Name', sortType: sortByNumericThenAlpha },
      {
        Header: 'Image',
        accessor: 'ImageUrl',
        disableSortBy: true,
        Cell: (event: any) => {
          const { row } = event;
          const imageUrl = this.getImageUrl(row?.original, false);

          return <img src={imageUrl} style={{ height: '40px' }} />;
        },
      },
      {
        Header: 'Last Updated',
        accessor: 'LastUpdatetimestamp',
        sortType: sortByNumericThenAlpha,
        Cell: (a: any) => {
          return a.value ? new Date(a.value).toLocaleDateString('en-CA') : '';
        },
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        Cell: (event: any) => {
          const { row } = event;
          return this.getActionMenu({ row, setShowEditModal, setEntity, setShowDeleteModal, mode });
        },
      },
    ];
  }

  getActionMenu = (event: any) => {
    const { row, setShowEditModal, setEntity, setShowDeleteModal, mode } = event;
    return (
      <>
        <div>
          <Authorize requires={[Permissions.WriteAdminTools]}>
            {mode === FORM_MODE.EDIT && (
              <>
                <ActionMenuDelete
                  onClick={() => {
                    setEntity(row.original);
                    setShowDeleteModal(true);
                  }}
                />
                <ActionMenuEdit
                  onClick={() => {
                    setEntity(row.original);
                    setShowEditModal(true);
                  }}
                />
              </>
            )}
            {mode === FORM_MODE.VIEW && (
              <ActionMenuView
                onClick={() => {
                  setEntity(row.original);
                  setShowEditModal(true); // TODO: showViewModal
                }}
              />
            )}
            <ActionMenuDownload onClick={() => this.handleDownload(row.original)} />
          </Authorize>
        </div>
      </>
    );
  };

  public getFilters = (props: any) => {
    const { sortBy, setSortBy, setSearchParams, search, setSearch, ...rest } = props;

    const sortByOptions = [
      { value: 'desc', label: 'Upload Date (Newest)' },
      { value: 'asc', label: 'Upload Date (Oldest)' },
    ];

    const defaultValue = sortByOptions.find((val) => {
      return val.value === sortBy;
    });

    return (
      <Row>
        {setSortBy && (
          <Col>
            <FormSelect
              className="avalanche-select"
              readOnly={false}
              name="sortBy"
              id="sortBy"
              label="Sort By"
              useMargin={true}
              options={sortByOptions}
              isMulti={false}
              isClearable={true}
              postChange={(e: any) => {
                setSearchParams((params: URLSearchParams) => {
                  if (e) {
                    params.set(AVALANCHE_PATH_IMAGES_PARAMS.SORT_BY, e.value);
                  } else {
                    params.delete(AVALANCHE_PATH_IMAGES_PARAMS.SORT_BY);
                  }

                  setSortBy(e?.value);

                  return params;
                });
              }}
              textValue={undefined}
              instruction={undefined}
              defaultValue={defaultValue}
            />
          </Col>
        )}
        <Col>
          <div className={'my-1'}>
            <Form.Label>
              <b>{'Search'}</b>
            </Form.Label>
            <input
              className="form-control"
              placeholder="Search..."
              id="search"
              name="search"
              onChange={(e: any) => {
                setSearchParams((params: URLSearchParams) => {
                  if (e?.target?.value) {
                    params.set(AVALANCHE_PATH_IMAGES_PARAMS.SEARCH, e.target.value);
                  } else {
                    params.delete(AVALANCHE_PATH_IMAGES_PARAMS.SEARCH);
                  }

                  setSearch(e.target.value);
                  return params;
                });
              }}
              value={search}
            />
          </div>
        </Col>
      </Row>
    );
  };

  private getImageUrl(entity: AvalanchePathImagesDTO, getOriginal = true): string {
    if (isValueEmpty(entity)) return '';

    const base64Image = getOriginal ? entity.OriginalObjectImage : entity.ReducedObjectImage;

    return `data:image/jpeg;base64,${base64Image}`;
  }

  private getCardImage(entity: AvalanchePathImagesDTO, showEditModal: any) {
    const imageUrl = this.getImageUrl(entity, false);

    const cardImgStyle: any = { cursor: 'auto', height: '200px', width: '200px', objectFit: 'contain' };
    if (showEditModal) cardImgStyle.cursor = 'pointer';

    return (
      <Card.Img
        variant="top"
        className={'mx-auto'}
        src="/assets/images/spinner.gif"
        onLoad={(e: any) => {
          loadImage(imageUrl).then(
            (img) => {
              if (img?.src) e.target.src = img?.src;
            },
            (e) => {
              console.error('Error loading image:', e);
            },
          );
        }}
        style={cardImgStyle}
        onClick={
          showEditModal
            ? () => {
                showEditModal(entity);
              }
            : undefined
        }
      />
    );
  }

  public getCards = (props: any) => {
    const { setShowEditModal, setEntity, setShowDeleteModal, mediaEntities, setSortBy, mode, ...rest } = props;

    const showEditModal = (entity: AvalanchePathImagesDTO) => {
      setEntity(entity);
      setShowEditModal(true);
    };

    return mediaEntities?.map((entity: AvalanchePathImagesDTO) => {
      let border = 'light';
      const style = { borderWidth: 'thin' };

      if (entity.IsDefault) {
        border = 'warning';
        style.borderWidth = 'medium';
      }

      return (
        <Col key={entity.Id}>
          <Card border={border} className="page-card mx-auto" style={style}>
            {this.getCardImage(entity, showEditModal)}
            <Card.Body>
              <Card.Title>{entity.Name}</Card.Title>
              <Card.Text>{entity.Notes}</Card.Text>
              <Card.Text>
                <small className="text-muted">
                  <strong>Uploaded:</strong> {new Date(entity.LastUpdatetimestamp).toLocaleDateString('en-CA')}
                </small>
                <br />
                <small className="text-muted">
                  <strong>Uploaded By:</strong> {entity.LastUpdateUserId}
                </small>
              </Card.Text>
            </Card.Body>
            <Card.Footer>
              {mode === FORM_MODE.EDIT && (
                <>
                  <ActionMenuDelete
                    onClick={() => {
                      setEntity(entity);
                      setShowDeleteModal(true);
                    }}
                  />
                  <ActionMenuEdit
                    onClick={() => {
                      showEditModal(entity);
                    }}
                  />
                </>
              )}
              {mode === FORM_MODE.VIEW && (
                <ActionMenuView
                  onClick={() => {
                    showEditModal(entity); // TODO: showViewModal
                  }}
                />
              )}
              <ActionMenuDownload onClick={() => this.handleDownload(entity)} />
            </Card.Footer>
          </Card>
        </Col>
      );
    });
  };

  getFields = (props: { readOnly: boolean; entity?: AvalanchePathImagesDTO; setImages?: any; images?: any }) => {
    const { readOnly, entity, setImages, images, ...rest } = props;

    return (
      <>
        {(!images || images?.length === 0) && entity && (
          <div style={{ position: 'relative', background: 'rgb(250, 250, 250)' }} className={'m-auto'}>
            {this.getCardImage(entity, null)}
            <i
              className="fas fa-download awp-img-btn"
              style={{ position: 'absolute', bottom: 5, left: 5, backgroundColor: 'black', color: 'white' }}
              title={'Download'}
              onClick={() => this.handleDownload(entity)}
            />
            <a
              onClick={() => {
                const image = new Image();
                image.src = this.getImageUrl(entity);

                const tempWindow = window.open('');
                tempWindow?.document.body.appendChild(image);
              }}
            >
              <i
                className="fas fa-lg fa-external-link-square-alt awp-img-btn"
                style={{ position: 'absolute', bottom: 5, right: 5, backgroundColor: 'black', color: 'white' }}
                title={'Full Screen'}
              />
            </a>
          </div>
        )}
        <Card.Body>
          {!readOnly && (
            <Row>
              <Col>
                <FormDropZone
                  label={`Upload Image (Max. ${bytesToMB(this.targetSize)} MB)`}
                  name="images"
                  id="images"
                  maxSize={this.targetSize}
                  preview={true}
                  onChange={(acceptedFiles): any => {
                    setImages?.(acceptedFiles);
                  }}
                  accept={{
                    'image/tiff': ['.tif', '.tiff'],
                    'image/jpg': ['.jpeg', '.jpg', '.jpe'],
                    'image/gif': ['.gif'],
                    'image/png': ['.png'],
                    'image/bmp': ['.bmp'],
                  }}
                  instruction={undefined}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <FormInput
                label="Name"
                name="Name"
                id="Name"
                readOnly={readOnly}
                instruction={undefined}
                onChange={undefined}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormTextArea
                label="Notes"
                name="Notes"
                id="Notes"
                readOnly={readOnly}
                rows={3}
                instruction={undefined}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormTagsInput
                id="Tags"
                name="Tags"
                label="Tags"
                readOnly={readOnly}
                instruction={'Press Enter to create new Tag'}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormCheckbox
                useMargin={true}
                readOnly={entity?.IsDefault ? true : readOnly}
                id="IsDefault"
                name="IsDefault"
                label="Default"
                onCheck={undefined}
              />
            </Col>
          </Row>
        </Card.Body>
      </>
    );
  };

  getValidations(props?: any) {
    const { mode, ...rest } = props;

    return Yup.object({
      Name: Yup.string()
        .label('Name')
        .max(100, '${label} must not exceed ${max} characters')
        .required('${label} is a required field'),
      Notes: Yup.string().label('Notes').max(1000, '${label} must not exceed ${max} characters'),
      images:
        mode === FORM_MODE.CREATE
          ? Yup.array().label('Upload Image').min(1, '${label} is a required field')
          : Yup.array(),
    });
  }

  mapInitialValues(props: { entity: AvalanchePathImagesDTO | undefined }): any {
    const { entity, ...rest } = props;

    return {
      ...entity,
      Name: entity?.Name ?? '',
      images: [],
    };
  }

  mapFormToDTO(props: { entity: AvalanchePathImagesDTO | undefined }): any {
    const { entity, ...rest } = props;

    return {
      ...rest,
      ...entity,
      IsDefault: isValueEmpty(entity?.IsDefault) ? false : entity?.IsDefault,
      Tags: isValueEmpty(entity?.Tags) ? [] : entity?.Tags,
    };
  }

  cleanFormValues(props: EntityProps): any {
    /* Do Nothing */
  }
}

export default AvalanchePathImagesService;
